import { Body } from '@qred/components-library';
import moment from 'moment';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import InputField from '~/components/shared/Input/InputField';
import NumberInputField from '~/components/shared/Input/NumberInputField';
import Stack from '~/components/shared/Layout/Stack';
import {
  getPersonalNumberPlaceholder,
  marketHasDateOfBirth,
  marketShouldShowCreditCheckCopyTooltip,
  personalNumberIsOnlyNumbers,
  PhoneNumberPlaceholders,
} from '~/constants/markets';
import { formatPersonalNumber } from '~/helpers/formatters.helper';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useDateFormatter from '~/hooks/useDateFormatter';
import useMarketConfig from '~/hooks/useMarketConfig';
import useTranslate from '~/hooks/useTranslate';
import { IAddressFields } from '~/interfaces/shared';
import { RootState } from '~/store';
import { trackCardApplicationActions } from '~/store/actions/analyticsActions';
import {
  updateApplicantAddress,
  updateApplicantData,
} from '~/store/slices/cardApplication.slice';
import { onChangeInputEvent } from '~/types/types';
import { Address } from './components/Address';
import { DateOfBirth } from './components/DateOfBirth';

export const PersonalInformation = () => {
  const translate = useTranslate();
  const dispatch = useAppDispatch();

  const formatDate = useDateFormatter();

  const {
    marketHasPersonalNumber,
    marketHasAddress,
    marketShouldAskApplicantForFullName,
  } = useMarketConfig();

  const {
    fullName,
    email,
    phone,
    personalNumber,
    dateOfBirth,
    addressFields,
  } = useSelector((state: RootState) => state.cardApplication.form.applicant);
  const { market, language } = useSelector((state: RootState) => state.intl);

  const onChange = (event: onChangeInputEvent) => {
    const { name, value } = event.target;

    dispatch(updateApplicantData({ [name]: value }));
  };

  const onBlur = (event: onChangeInputEvent) => {
    const { name, value } = event.target;

    dispatch(
      trackCardApplicationActions({
        actionName: `${name}_change`,
      })
    );

    dispatch(updateApplicantData({ [name]: value.trim() }));
  };

  const onAddressFieldChange = (partialAddress: IAddressFields) => {
    dispatch(updateApplicantAddress(partialAddress));
  };

  const dateOfBirthOnChangeHandler = useCallback(
    (date: Date | null) => {
      if (date instanceof Date) {
        dispatch(
          updateApplicantData({
            dateOfBirth: moment(date).format('YYYY-MM-DD'),
          })
        );
        dispatch(
          trackCardApplicationActions({
            actionName: `date_of_birth_change`,
          })
        );
      }
    },
    [dispatch]
  );

  const tooltipCopy = marketShouldShowCreditCheckCopyTooltip[market] ? (
    <Body size="md">
      {translate('Onboarding.TooltipCreditCheckReportGenerated', {
        TooltipCreditCheckImpactWorthiness: (
          <Body size="md" weight={600}>
            {translate('Onboarding.TooltipCreditCheckImpactWorthiness')}
          </Body>
        ),
      })}
    </Body>
  ) : (
    <></>
  );

  return (
    <Stack spacing="md">
      {marketShouldAskApplicantForFullName && (
        <InputField
          name="fullName"
          dataCy="personal_information_step_full_name"
          label={translate('FullName') as string}
          placeholder={translate('FullName') as string}
          value={fullName || ''}
          onChange={onChange}
          onBlur={onBlur}
          validationType="FullName"
        />
      )}

      <InputField
        name="email"
        type="email"
        label={translate('Email') as string}
        onChange={onChange}
        onBlur={onBlur}
        value={email || ''}
        validationType="Email"
        placeholder={translate('Onboarding.EmailPlaceholder') as string}
      />

      <NumberInputField
        tel
        name="phone"
        label={translate('Phone') as string}
        onChange={onChange}
        onBlur={onBlur}
        value={phone || ''}
        validationType="Phone"
        placeholder={PhoneNumberPlaceholders[market]}
      />

      {marketHasPersonalNumber && (
        <InputField
          name="personalNumber"
          dataCy="personal_information_step_personal_number"
          label={translate('PersonalNumber') as string}
          explanation={tooltipCopy}
          onChange={onChange}
          onBlur={onBlur}
          inputMode={personalNumberIsOnlyNumbers(market) ? 'numeric' : 'text'}
          placeholder={getPersonalNumberPlaceholder(market, language)}
          value={formatPersonalNumber(personalNumber || '', market)}
          validationType="PersonalNumber"
        />
      )}

      {marketHasDateOfBirth[market] && (
        <DateOfBirth
          useModal
          dateOfBirthOnChangeHandler={dateOfBirthOnChangeHandler}
          value={
            (dateOfBirth &&
              formatDate(dateOfBirth, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })) ||
            ''
          }
          openOnFocus
        />
      )}

      {marketHasAddress && (
        <Address
          addressFields={addressFields}
          onAddressFieldChange={onAddressFieldChange}
          showTooltipHomeAddressExplanation
        />
      )}
    </Stack>
  );
};
